import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { InitialService } from './services/initial.service';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { apiList } from './services/apiList.service';
import { provideServiceWorker } from '@angular/service-worker';
import { AutocompleteOffDirective } from './core/direactives/autocomplete-off.directive';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { setInterceptor } from './core/interceptors/set-interceptor.service';
import { getInterceptor } from './core/interceptors/get-interceptor.service';
import { applyDocumentLanguage } from './core/utils/language-storage.util';
import { I18nService } from './services/i18n.service';
import { ServerClockService } from './services/server-clock.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideClientHydration(withEventReplay(), withIncrementalHydration()),
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideHttpClient(withFetch(), withInterceptors([getInterceptor, setInterceptor])),

    importProvidersFrom(NgxUiLoaderModule),

    provideAppInitializer(async () => {
      const platformId = inject(PLATFORM_ID);
      const isBrowser = isPlatformBrowser(platformId);
      const initialService = inject(InitialService);
      const i18n = inject(I18nService);
      const document = inject(DOCUMENT);
      const serverClock = inject(ServerClockService);

      const lang = i18n.resolveInitialLanguage();
      await i18n.load(lang);

      if (isBrowser) {
        applyDocumentLanguage(lang, document);
        await serverClock.sync();
        setTimeout(() => {
          initialService.getLanguages();
          initialService.setUpGoogleScript().catch(() => {});
        }, 0);
      }
    }),

    provideAnimationsAsync(),

    provideServiceWorker('/firebase-sw.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:3000',
    }),
    MessageService,
    DialogService,
    apiList,
    ConfirmationService,
    DynamicDialogRef,
    AutocompleteOffDirective,
  ],
};
